<script setup lang="ts">
  import { computed } from 'vue';
  import { Switch } from '@/shared/ui';
  import {
    changeBreakdown,
    changeCountLabel,
    type CorrectedWord,
    type FillLogsPaperTarget,
    type FillLogsText,
  } from '../model/fill-logs-text';
  import type { FillLogsResponseView } from '../model/fill-logs-timeline';
  import FillLogsAllFields from './FillLogsAllFields.vue';
  import FillLogsChangeList from './FillLogsChangeList.vue';

  const props = defineProps<{
    text: FillLogsText;
    current: FillLogsResponseView;
    showAll: boolean;
    correctedWord: CorrectedWord;
  }>();

  const emit = defineEmits<{
    (e: 'update:showAll', value: boolean): void;
    (e: 'show', target: FillLogsPaperTarget): void;
  }>();

  const breakdown = computed(() => changeBreakdown(props.text.changes, props.correctedWord));
  const isErp = computed(() => props.current.kind === 'erp');
  const where = computed(() =>
    isErp.value
      ? `no preenchimento do ${props.current.authorName}`
      : `na resposta ${props.current.number}, de ${props.current.authorName}`,
  );
</script>

<template>
  <div class="overflow-hidden rounded-md border border-gray-02 bg-white-01 text-gray-07">
    <div
      class="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 px-6 py-4"
      :class="{ 'border-b border-gray-02': showAll || text.changes.length > 0 }"
    >
      <p
        class="min-w-0 flex-[1_1_260px] text-sm/[22px] text-gray-03"
        data-testid="fill-logs-text-summary"
      >
        <template v-if="showAll && isErp">
          Todos os campos como ficaram no <b class="font-semibold text-gray-07">preenchimento do {{ current.authorName }}</b>.
          O que ele preencheu tem a barra colorida.
        </template>
        <template v-else-if="showAll">
          Todos os campos como ficaram na <b class="font-semibold text-gray-07">resposta {{ current.number }}</b>.
          O que mudou nesta resposta tem a barra colorida.
        </template>
        <template v-else-if="text.changes.length > 0">
          <b class="font-semibold text-gray-07">{{ changeCountLabel(text.changes.length) }}</b>
          {{ where }}: {{ breakdown }}.
        </template>
        <template v-else>
          Nenhuma mudança visível {{ where }}.
        </template>
      </p>
      <label class="flex shrink-0 cursor-pointer items-center gap-2.5 text-sm text-gray-07">
        <Switch
          size="lg"
          :model-value="showAll"
          data-testid="fill-logs-show-all-switch"
          @update:model-value="emit('update:showAll', $event)"
        />
        Mostrar todos os campos
      </label>
    </div>
    <FillLogsAllFields
      v-if="showAll"
      :sections="text.sections"
      :corrected-word="correctedWord"
      @show="emit('show', $event)"
    />
    <FillLogsChangeList
      v-else-if="text.changes.length > 0"
      :items="text.changes"
      @show="emit('show', $event)"
    />
  </div>
</template>
